import React, { useState, useEffect } from "react";
import { 
  Sparkles, 
  History, 
  CheckCircle2, 
  Trash2, 
  MessageSquare,
  Zap,
  Save,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import { useToast } from "../context/ToastContext";

const PromptVersioning = () => {
  const [prompts, setPrompts] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [versionName, setVersionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // 'current' or 'history'
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  const loadPrompts = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getInstructionVersions();
      setPrompts(response.versions || []);
      
      const active = (response.versions || []).find(p => p.is_active);
      if (active && activeTab === 'current') {
        setCurrentPrompt(active.content);
      }
    } catch (error) {
      console.error("Error loading prompts:", error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, setCurrentPrompt]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const handleCreatePrompt = async () => {
    if (!currentPrompt.trim()) return;
    if (prompts.length >= 5) {
      showToast("You have reached the limit of 5 prompts. Please delete an older version to add a new one.", "warning");
      return;
    }

    setSaving(true);
    try {
      const name = versionName.trim();
      await api.createInstructions(currentPrompt, name);
      setVersionName("");
      await loadPrompts();
      setActiveTab("history");
      showToast("New prompt version created and activated!", "success");
    } catch (error) {
      showToast(error.message || "Failed to create prompt version", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    if (currentActive) return; // Already is_active
    
    try {
      await api.activateInstruction(id);
      await loadPrompts();
      showToast("Instruction activated successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to activate prompt", "error");
    }
  };

  const handleDelete = (id, isActive) => {
    if (isActive) {
      showToast("Cannot delete the currently active prompt. Please activate another one first.", "error");
      return;
    }
    setPromptToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!promptToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteInstruction(promptToDelete);
      await loadPrompts();
      setIsDeleteModalOpen(false);
      showToast("Prompt version deleted successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to delete prompt", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImprove = async () => {
    if (!currentPrompt.trim()) return;
    setImproving(true);
    try {
      const response = await api.improvePrompt(currentPrompt);
      if (response.success) {
        setCurrentPrompt(response.improved_prompt);
      }
    } catch (error) {
      showToast("Failed to improve prompt", "error");
    } finally {
      setImproving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn border-t border-gray-100">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        <PageHeader 
          title="Neural Directives"
          description="Orchestrate the personality and operational parameters of your digital agent with precision."
        />
          
          <div className="flex justify-center mt-0">
            <div className="flex bg-white/50 backdrop-blur-md p-1.5 rounded-2xl border border-gray-100 shadow-sm w-fit mx-auto">
                <button 
                  onClick={() => setActiveTab("current")}
                  className={`px-8 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
                    activeTab === 'current' 
                    ? 'bg-gray-900 text-white shadow-lg shadow-gray-200' 
                    : 'text-gray-400 hover:text-gray-900 hover:bg-white'
                  }`}
                >
                  <Zap size={14} /> Editor
                </button>
                <button 
                  onClick={() => setActiveTab("history")}
                  className={`px-8 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
                    activeTab === 'history' 
                    ? 'bg-gray-900 text-white shadow-lg shadow-gray-200' 
                    : 'text-gray-400 hover:text-gray-900 hover:bg-white'
                  }`}
                >
                  <History size={14} /> History
                  <span className={`px-2 py-0.5 rounded-full text-[9px] ml-1 font-black ${activeTab === 'history' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-400'}`}>
                    {prompts.length}/5
                  </span>
                </button>
            </div>
          </div>

        {activeTab === 'current' ? (
          <div className="w-full">
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8">
                <button 
                  onClick={handleImprove}
                  disabled={improving || !currentPrompt.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50 text-[10px] font-bold uppercase tracking-wider border border-blue-100"
                >
                  <Sparkles size={14} className={improving ? "animate-spin" : ""} />
                  {improving ? "Refining..." : "Magic Improve"}
                </button>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                  <MessageSquare size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">System Prompt</h3>
                  <p className="text-[11px] font-medium text-gray-400">Define how your AI should interact</p>
                </div>
              </div>

              <textarea
                value={currentPrompt}
                onChange={(e) => setCurrentPrompt(e.target.value)}
                placeholder="e.g. You are a helpful assistant for EasyChat..."
                className="w-full h-[400px] p-6 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm leading-relaxed"
              />

               <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 w-full">
                <div className="w-full md:flex-1 group">
                  <input 
                    type="text"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    placeholder="Version name (e.g. V2 - Friendly)"
                    className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-gray-900 shadow-inner"
                  />
                </div>
                <button 
                  onClick={handleCreatePrompt}
                  disabled={saving || !currentPrompt.trim() || !versionName.trim()}
                  className="flex items-center justify-center gap-2 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest shadow-md shadow-gray-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 flex-shrink-0"
                >
                  <Save size={16} />
                  {saving ? "Saving..." : "Save & Activate"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full animate-slideIn">
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden">
              <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Prompt History</h3>
                <span className="text-[11px] font-medium text-gray-400 font-medium">{prompts.length} Versions Saved</span>
              </div>
              
              <div className="divide-y divide-gray-50">
                {prompts.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    <History size={48} className="mx-auto mb-4 opacity-20" />
                    <p>No versions saved yet.</p>
                  </div>
                ) : (
                  prompts.map((p) => (
                    <div key={p.id} className={`p-6 flex items-center justify-between gap-6 transition-colors ${p.is_active ? 'bg-blue-50/30' : 'hover:bg-gray-50/50'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-bold text-gray-900 truncate">{p.version_name}</h4>
                          {p.is_active && (
                            <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                              <CheckCircle2 size={10} /> Active
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-medium text-gray-400 truncate mr-12 font-mono">
                          {p.content}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2">
                          Last updated: {new Date(p.updated_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleToggleActive(p.id, p.is_active)}
                          className={`flex items-center gap-2 transition-all ${p.is_active ? 'text-blue-500' : 'text-gray-300 hover:text-gray-400'}`}
                          title={p.is_active ? "Currently Active" : "Click to activate"}
                        >
                          {p.is_active ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                        </button>

                        <button 
                          onClick={() => handleDelete(p.id, p.is_active)}
                          className={`p-2 rounded-xl transition-all ${p.is_active ? 'opacity-0 cursor-default' : 'text-gray-300 hover:text-red-500 hover:bg-red-50'}`}
                          disabled={p.is_active}
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <p className="mt-6 text-center text-xs text-gray-400">
              Only the active prompt governs the AI agent's behavior. Max 5 versions allowed.
            </p>
          </div>
        )}
      </div>
      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Purge Neural Directive?"
        message="This action will permanently purge this instruction sequence from the neural history. Are you prepared to proceed with the deletion?"
      />
    </div>
  );
};

export default PromptVersioning;
